import { useCallback, useEffect, useMemo, useState } from 'react';

export const ONBOARDING_STEP_KEYS = [
  'appInstallGuide',
  'workScheduleConfigured',
  'clientCreated',
  'qrPaymentsSetup',
  'serviceCreated',
  'stockItemCreated',
  'visitCreated',
  'visitCompleted',
  'reservationEnabled',
  'publicReservationSetup',
  'currencyConfigured',
] as const;

export type OnboardingStepKey = (typeof ONBOARDING_STEP_KEYS)[number];

const ONBOARDING_GUIDE_STORAGE_KEY = 'onboarding-guide-dismissed-version';
const ONBOARDING_GUIDE_EVENT = 'onboarding-guide-visibility-change';
export const APP_INSTALL_DONE_STORAGE_KEY = 'onboarding-app-install-done';

const getDismissedVersion = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(ONBOARDING_GUIDE_STORAGE_KEY);
};

const emitVisibilityChange = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(ONBOARDING_GUIDE_EVENT));
};

export const useOnboardingGuideVisibility = (stepKeys: readonly string[]) => {
  const version = useMemo(() => stepKeys.join('|'), [stepKeys]);
  const getIsVisible = useCallback(() => getDismissedVersion() !== version, [version]);
  const [isVisible, setIsVisible] = useState(getIsVisible);

  useEffect(() => {
    setIsVisible(getIsVisible());
  }, [getIsVisible]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncVisibility = () => {
      setIsVisible(getIsVisible());
    };

    window.addEventListener(ONBOARDING_GUIDE_EVENT, syncVisibility);
    window.addEventListener('storage', syncVisibility);

    return () => {
      window.removeEventListener(ONBOARDING_GUIDE_EVENT, syncVisibility);
      window.removeEventListener('storage', syncVisibility);
    };
  }, [getIsVisible]);

  const dismissGuide = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ONBOARDING_GUIDE_STORAGE_KEY, version);
    setIsVisible(false);
    emitVisibilityChange();
  };

  const showGuide = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(ONBOARDING_GUIDE_STORAGE_KEY);
    setIsVisible(true);
    emitVisibilityChange();
  };

  return {
    isVisible: isVisible,
    dismissGuide,
    showGuide,
    version,
  };
};
